/**
 * 🏠 LANDING PAGE — COACHDIA ULTRA PREMIUM (Iter298 — LIGHT THEME)
 * ================================================================
 *
 * Refonte complète : 12 sections, fond ivoire premium + accents or,
 * langage simple, hiérarchie visuelle forte, mobile-first.
 *
 * IDs de section synchronisés avec Navbar (sectionIds) :
 *   accueil · pourquoi · piliers · coach-ia · temoignages · plans
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, Brain, Target, Award, MessageSquare, Mic,
  Users, CheckCircle, XCircle, Quote, Lock, Layers, Lightbulb,
  Compass, Zap, Eye, Headphones, AlertTriangle, Loader2,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import TestimonialsSection from '../components/TestimonialsSection';
import HeroDemoVideo from '../components/HeroDemoVideo';
import '../styles/premium-animations.css';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

/* ─────────────────── Atoms ─────────────────── */

const Kicker = ({ children }) => (
  <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase font-semibold mb-4 text-[#B7901F]">
    {children}
  </p>
);

const SignaturePhrase = ({ children }) => (
  <p className="text-xl sm:text-2xl lg:text-3xl font-serif leading-tight tracking-tight text-[#1C1147]">
    {children}
  </p>
);

const PromiseLine = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[#3A3252]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
    {children}
  </span>
);

const SectionDivider = () => (
  <div className="my-20 sm:my-28 flex items-center gap-4 max-w-6xl mx-auto px-4" aria-hidden>
    <span className="flex-1 h-px bg-gradient-to-r from-transparent via-[#1C1147]/15 to-transparent" />
    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
    <span className="flex-1 h-px bg-gradient-to-r from-transparent via-[#1C1147]/15 to-transparent" />
  </div>
);

const RuleRow = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-[#3A3252] text-base sm:text-lg leading-relaxed">
    <span className="shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
      <Icon className="w-3.5 h-3.5 text-[#B7901F]" />
    </span>
    <span>{children}</span>
  </li>
);

const PillarCard = ({ icon: Icon, title }) => (
  <motion.div
    {...fadeInUp}
    className="group relative overflow-hidden rounded-2xl bg-white border border-[#EDE7D7] p-5 hover:border-[#D4AF37] hover:-translate-y-1 transition-all duration-300"
    style={{ boxShadow: '0 2px 12px rgba(28,17,71,0.04)' }}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFE9A0] to-[#D4AF37] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#1C1147]" />
      </div>
      <h4 className="text-base sm:text-lg font-semibold text-[#1C1147]">{title}</h4>
    </div>
  </motion.div>
);

const ContrastRow = ({ before, after }) => (
  <motion.div {...fadeInUp} className="grid sm:grid-cols-2 gap-3 sm:gap-4">
    <div className="rounded-2xl border border-[#E5DFCE] bg-white px-5 py-4 flex items-start gap-3">
      <XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" />
      <span className="text-[#7A6F58] text-sm sm:text-base line-through decoration-[#A89C7B]/40">{before}</span>
    </div>
    <div
      className="rounded-2xl border-2 border-[#D4AF37] bg-gradient-to-br from-[#FFF8E0] to-[#FFFCEC] px-5 py-4 flex items-start gap-3"
      style={{ boxShadow: '0 4px 18px rgba(212,175,55,0.10)' }}
    >
      <CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" />
      <span className="text-[#1C1147] text-sm sm:text-base font-medium">{after}</span>
    </div>
  </motion.div>
);

const PriceCard = ({ tier, sessions, price, highlighted, testid }) => (
  <motion.div
    {...fadeInUp}
    data-testid={testid}
    className={`relative rounded-3xl p-7 sm:p-8 border-2 transition-all hover:-translate-y-1 ${
      highlighted
        ? 'bg-gradient-to-br from-[#FFE9A0] via-[#F3D060] to-[#D4AF37] border-[#B7901F] text-[#1C1147]'
        : 'bg-white border-[#EDE7D7] text-[#1C1147] hover:border-[#D4AF37]'
    }`}
    style={{
      boxShadow: highlighted
        ? '0 18px 44px -14px rgba(212,175,55,0.45)'
        : '0 4px 18px rgba(28,17,71,0.05)',
    }}
  >
    {highlighted && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1C1147] text-[#F3D060] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
        Recommandé
      </span>
    )}
    <p
      className={`text-[10px] tracking-[0.3em] uppercase font-semibold mb-3 ${
        highlighted ? 'text-[#1C1147]/75' : 'text-[#B7901F]'
      }`}
    >
      {tier}
    </p>
    <p className="font-serif text-5xl sm:text-6xl font-bold tabular-nums leading-none mb-2 text-[#1C1147]" data-testid={`${testid}-sessions`}>
      {sessions}
    </p>
    <p className={`text-sm mb-6 ${highlighted ? 'text-[#1C1147]/75' : 'text-[#5A5266]'}`}>
      sessions / mois
    </p>
    <p className="text-3xl font-serif font-semibold mb-6 text-[#1C1147]" data-testid={`${testid}-price`}>
      {price}
      <span className="text-base font-normal opacity-70"> /mois</span>
    </p>
    <Link
      to="/subscription"
      className={`inline-flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold transition-all ${
        highlighted
          ? 'bg-[#1C1147] text-[#F3D060] hover:bg-[#2A1F66]'
          : 'bg-gradient-to-r from-[#D4AF37] to-[#F3D060] text-[#1C1147] hover:opacity-90'
      }`}
    >
      Commencer <ArrowRight className="w-4 h-4" />
    </Link>
  </motion.div>
);

/* ─── Dynamic pricing grid (source de vérité unique : /api/public/subscription-packs) ─── */

const PricingGrid = () => {
  const [state, setState] = useState({ status: 'loading', packs: [], error: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/public/subscription-packs`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Erreur ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setState({ status: 'ready', packs: data.packs || [], error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', packs: [], error: err.message || 'Erreur réseau' });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (state.status === 'loading') {
    return (
      <div
        className="flex items-center justify-center py-16 text-[#5A5266]"
        data-testid="home-pricing-loading"
      >
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        Chargement des offres…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div
        className="rounded-2xl border-2 border-[#E0B8B8] bg-[#FBF1F1] p-6 sm:p-8 text-[#7A1F1F] flex items-start gap-3"
        data-testid="home-pricing-error"
      >
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <p className="font-semibold mb-1">Tarifs temporairement indisponibles</p>
          <p className="text-sm opacity-90">{state.error}</p>
          <p className="text-xs mt-2 opacity-70">
            Merci de réessayer dans un instant. Aucun prix par défaut n'est affiché afin de garantir
            l'exactitude des tarifs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-3 gap-5 mb-8" data-testid="home-pricing-grid">
      {state.packs.map((p) => (
        <PriceCard
          key={p.pack_id}
          tier={p.name}
          sessions={p.sessions_quota}
          price={p.price_display}
          highlighted={p.is_popular}
          testid={`pricing-card-${p.pack_id}`}
        />
      ))}
    </div>
  );
};

/* ─── Premium pillar badges (différenciation IA / Humain) ─── */

const AIBadge = ({ children }) => (
  <span
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm sm:text-base mb-5"
    style={{
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      background: 'linear-gradient(135deg, #6D28D9, #8B5CF6, #A78BFA)',
      color: '#FFFFFF',
      boxShadow: '0 6px 22px -6px rgba(109,40,217,0.45), inset 0 1px 0 rgba(255,255,255,0.25)',
      textShadow: '0 1px 2px rgba(49,20,90,0.35)',
    }}
    data-testid="badge-coach-ia"
  >
    <Brain className="w-4 h-4" />
    {children}
  </span>
);

const HumanBadge = ({ children }) => (
  <span
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm sm:text-base mb-5"
    style={{
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      background: 'linear-gradient(135deg, #B7901F, #D4AF37, #F3D060)',
      color: '#1C1147',
      boxShadow: '0 6px 22px -6px rgba(212,175,55,0.50), inset 0 1px 0 rgba(255,255,255,0.45)',
    }}
    data-testid="badge-coaching-humain"
  >
    <Users className="w-4 h-4" />
    {children}
  </span>
);

/* ─────────────────────────── Page ─────────────────────────── */

const LandingPage = () => {
  return (
    <div className="min-h-screen relative bg-[#FAF7EF]" data-testid="landing-page">
      {/* Halos lumineux fixes très doux */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-[#FFE9A0]/40 blur-3xl" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#FFF1C5]/50 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[700px] h-[700px] rounded-full bg-[#FFFCEC]/60 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1400px]">
        <Navbar />

        {/* ─── 1. HERO ─── */}
        <section
          id="accueil"
          className="relative px-4 pt-28 sm:pt-32 pb-24 sm:pb-32 max-w-7xl mx-auto"
          data-testid="home-hero"
        >
          <div className="grid lg:grid-cols-12 gap-10 xl:gap-14 items-start">
            <motion.div {...fadeInUp} className="lg:col-span-7 max-w-3xl">
              <Kicker>CoachDIA</Kicker>
              <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl text-[#1C1147] leading-[1.05] tracking-tight mb-8">
                L'architecture du
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B7901F] via-[#D4AF37] to-[#F3D060]">
                  leadership mesurable.
                </span>
              </h1>
              <SignaturePhrase>La plupart des managers ne progressent pas.</SignaturePhrase>
              <p className="text-xl sm:text-2xl lg:text-3xl font-serif leading-tight tracking-tight text-[#7A6F58] mt-3 mb-10">
                Parce qu'ils ne sont jamais évalués sur leurs décisions.
              </p>
              <div className="space-y-3 mb-10 max-w-2xl">
                <p className="text-lg sm:text-xl text-[#1C1147] leading-relaxed">
                  <strong>CoachDIA change cette règle.</strong>
                </p>
                <p className="text-lg sm:text-xl text-[#3A3252] leading-relaxed">
                  Ce n'est pas une plateforme de formation.
                  <br />
                  <span className="text-[#B7901F] font-semibold">
                    C'est un système qui transforme vos compétences en preuves mesurables.
                  </span>
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-12">
                <PromiseLine>Vous comprenez</PromiseLine>
                <PromiseLine>Vous vous entraînez</PromiseLine>
                <PromiseLine>Vous êtes challengé</PromiseLine>
                <PromiseLine>Vous êtes validé sur preuves</PromiseLine>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#5A5266]">
                <span className="inline-flex items-center gap-2"><Award className="w-4 h-4 text-[#B7901F]" /> 20 modules certifiants</span>
                <span className="inline-flex items-center gap-2"><Mic className="w-4 h-4 text-[#B7901F]" /> Coach IA vocal temps réel</span>
                <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-[#B7901F]" /> Coachs certifiés</span>
                <span className="inline-flex items-center gap-2"><Eye className="w-4 h-4 text-[#B7901F]" /> Analyse IA 360° post-session</span>
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/subscription"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3D060] text-[#1C1147] font-bold hover:opacity-95 transition-all"
                  style={{ boxShadow: '0 10px 30px -8px rgba(212,175,55,0.55)' }}
                  data-testid="hero-cta-start"
                >
                  Commencer maintenant <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/information-publique"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37]/50 text-[#1C1147] hover:bg-white transition-all"
                >
                  En savoir plus
                </Link>
              </div>
            </motion.div>

            {/* Right column : Demo video */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 w-full max-w-[560px] mx-auto lg:mx-0 lg:ml-auto mt-2 lg:mt-11"
            >
              <HeroDemoVideo />
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ─── 2. CE QUE COACHDIA FAIT RÉELLEMENT (Vision) ─── */}
        <section id="pourquoi" className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-12">
            <Kicker>Ce que CoachDIA fait réellement</Kicker>
            <SignaturePhrase>Ici, rien n'est validé sur intention.</SignaturePhrase>
            <p className="text-xl sm:text-2xl font-serif text-[#B7901F] leading-tight tracking-tight mt-2">
              Chaque compétence est validée uniquement lorsqu'elle est démontrée dans l'action.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5 mb-14">
            <ContrastRow before="Une progression ressentie" after="Une progression mesurable" />
            <ContrastRow before="Des décisions supposées" after="Des décisions analysées" />
            <ContrastRow before="Un feedback vague et tardif" after="Un feedback structuré après chaque interaction" />
            <ContrastRow before="Une montée en compétence floue" after="Une montée en compétence observable dans le temps" />
          </div>
          <motion.div
            {...fadeInUp}
            className="rounded-3xl bg-white border-2 border-[#D4AF37]/30 p-8 sm:p-10"
            style={{ boxShadow: '0 8px 32px rgba(28,17,71,0.06)' }}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-5">
              Trois règles structurantes
            </p>
            <ul className="space-y-4">
              <RuleRow icon={Lock}>Pas de validation sans preuve.</RuleRow>
              <RuleRow icon={Zap}>Pas de progression sans confrontation.</RuleRow>
              <RuleRow icon={Compass}>Pas de transformation sans exigence.</RuleRow>
            </ul>
          </motion.div>
        </section>

        <SectionDivider />

        {/* ─── 3. UN SYSTÈME, PAS UN PARCOURS ─── */}
        <section className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-10">
            <Kicker>Système &gt; Parcours</Kicker>
            <SignaturePhrase>Vous n'accédez pas à du contenu.</SignaturePhrase>
            <p className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#B7901F] leading-tight tracking-tight mt-2">
              Vous entrez dans une architecture de transformation.
            </p>
          </motion.div>
        </section>

        {/* ─── 4. MODULES + PILIERS (Formation) ─── */}
        <section id="piliers" className="px-4 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-white border border-[#EDE7D7] p-8 sm:p-10"
              style={{ boxShadow: '0 8px 32px rgba(28,17,71,0.05)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFE9A0] to-[#D4AF37] flex items-center justify-center shadow-md">
                  <Layers className="w-6 h-6 text-[#1C1147]" />
                </div>
                <p className="font-serif text-5xl text-[#B7901F] tabular-nums">20</p>
              </div>
              <h3 className="text-2xl font-serif text-[#1C1147] mb-2">Modules certifiants</h3>
              <p className="text-[#5A5266] mb-5 leading-relaxed">
                Chaque module est conçu comme une brique de transformation.
              </p>
              <ul className="space-y-2.5 text-[#3A3252] text-sm sm:text-base">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Objectifs pédagogiques explicites</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Entraînements écrits et vocaux</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Évaluation sur preuves</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Validation formelle</li>
              </ul>
            </motion.div>
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-gradient-to-br from-[#FFF8E0] to-white border-2 border-[#D4AF37]/30 p-8 sm:p-10"
              style={{ boxShadow: '0 8px 32px rgba(212,175,55,0.10)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1C1147] to-[#3A2680] flex items-center justify-center shadow-md">
                  <Target className="w-6 h-6 text-[#F3D060]" />
                </div>
                <p className="font-serif text-5xl text-[#B7901F] tabular-nums">5</p>
              </div>
              <h3 className="text-2xl font-serif text-[#1C1147] mb-2">Piliers du leadership moderne</h3>
              <p className="text-[#5A5266] mb-5 leading-relaxed">Les fondations du management contemporain.</p>
              <div className="grid grid-cols-1 gap-2.5">
                <PillarCard icon={Users} title="Management des équipes" />
                <PillarCard icon={MessageSquare} title="Communication de leadership" />
                <PillarCard icon={Zap} title="Efficacité opérationnelle" />
                <PillarCard icon={Lightbulb} title="Leadership inspirant" />
                <PillarCard icon={Brain} title="Manager avec l'IA" />
              </div>
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ─── 5. OWEN — COACH IA ─── */}
        <section id="coach-ia" className="px-4 max-w-6xl mx-auto" data-testid="home-owen-section">
          <motion.div {...fadeInUp} className="max-w-3xl mb-10">
            <AIBadge>Le Coach IA</AIBadge>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1C1147] leading-tight tracking-tight mb-3">
              Owen.
            </h2>
            <p className="text-xl sm:text-2xl font-serif text-[#B7901F]">
              Exigeant. Structurant. Décisionnel.
            </p>
            <p className="text-lg text-[#3A3252] leading-relaxed mt-5">
              Owen n'est pas un assistant. C'est un système pédagogique capable de comprendre votre raisonnement,
              challenger vos réponses, identifier vos angles morts et valider votre progression sur preuves.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5 mb-10">
            <motion.div
              {...fadeInUp}
              className="rounded-2xl bg-white border border-[#EDE7D7] p-6 hover:border-[#D4AF37] transition-all"
              style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-3">
                Une interaction réelle
              </p>
              <ul className="space-y-2.5 text-[#3A3252]">
                <li className="flex gap-2 items-start"><Mic className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Conversation vocale naturelle</li>
                <li className="flex gap-2 items-start"><MessageSquare className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Jeux de rôle immersifs</li>
                <li className="flex gap-2 items-start"><Sparkles className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Feedback structuré immédiat</li>
              </ul>
            </motion.div>
            <motion.div
              {...fadeInUp}
              className="rounded-2xl bg-white border border-[#EDE7D7] p-6 hover:border-[#D4AF37] transition-all"
              style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-3">
                Une autorité pédagogique claire
              </p>
              <p className="text-[#3A3252] mb-4">
                Votre progression n'est jamais supposée. Elle est validée à :
              </p>
              <div className="space-y-2.5">
                {[
                  { pct: 40, label: 'émergence' },
                  { pct: 60, label: 'consolidation' },
                  { pct: 100, label: 'maîtrise' },
                ].map((l) => (
                  <div key={l.pct} className="flex items-center gap-3">
                    <span className="w-12 text-right tabular-nums font-serif text-xl font-bold text-[#B7901F]">
                      {l.pct}%
                    </span>
                    <div className="flex-1 h-1.5 bg-[#EDE7D7] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#B7901F] via-[#D4AF37] to-[#F3D060]"
                        style={{ width: `${l.pct}%` }}
                      />
                    </div>
                    <span className="text-[#5A5266] text-sm">{l.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ─── 6. COACHING HUMAIN AUGMENTÉ ─── */}
        <section id="coaching-humain" className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-10">
            <HumanBadge>Coaching Humain Augmenté</HumanBadge>
            <SignaturePhrase>L'IA ne remplace pas le coaching humain.</SignaturePhrase>
            <p className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#B7901F] leading-tight tracking-tight mt-2">
              Elle le rend mesurable.
            </p>
            <p className="text-lg text-[#3A3252] leading-relaxed mt-5">
              Accédez à un réseau de coachs certifiés, sélectionnés pour leur exigence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { n: 1, t: 'Session vidéo structurée', d: 'Cadre clair, agenda partagé.' },
              { n: 2, t: 'Échange réel sur vos enjeux', d: 'Plongée dans le concret.' },
              { n: 3, t: 'Analyse IA post-session', d: 'Automatique, immédiate.' },
            ].map((s) => (
              <motion.div
                key={s.n}
                {...fadeInUp}
                className="rounded-2xl bg-white border border-[#EDE7D7] p-6 relative"
                style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
              >
                <span className="absolute top-5 right-5 font-serif text-4xl text-[#D4AF37]/30">{s.n}</span>
                <h4 className="font-semibold text-[#1C1147] mb-2">{s.t}</h4>
                <p className="text-sm text-[#5A5266] leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeInUp}
            className="rounded-3xl bg-gradient-to-br from-[#FFE9A0] via-[#F3D060] to-[#D4AF37] p-8 sm:p-10 text-[#1C1147]"
            style={{ boxShadow: '0 12px 36px -10px rgba(212,175,55,0.45)' }}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold mb-4 text-[#1C1147]/85">
              Ce que vous obtenez
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div>
                <p className="font-serif text-xl font-semibold mb-1">Rapport global</p>
                <p className="text-sm text-[#1C1147]/75">Synthèse claire et structurée.</p>
              </div>
              <div>
                <p className="font-serif text-xl font-semibold mb-1">Feedback pour vous</p>
                <p className="text-sm text-[#1C1147]/75">Confidentiel, actionnable.</p>
              </div>
              <div>
                <p className="font-serif text-xl font-semibold mb-1">Feedback pour le coach</p>
                <p className="text-sm text-[#1C1147]/75">Confidentiel, dédié.</p>
              </div>
            </div>
            <p className="font-serif text-lg sm:text-xl border-t border-[#1C1147]/20 pt-5">
              Chaque session produit :{' '}
              <strong>de la clarté · des décisions · des axes d'action concrets.</strong>
            </p>
          </motion.div>
        </section>

        <SectionDivider />

        {/* ─── 7. ÉVALUATION SUR PREUVES ─── */}
        <section className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-10">
            <Kicker>Évaluation sur preuves</Kicker>
            <SignaturePhrase>Vous n'êtes pas évalué sur ce que vous comprenez.</SignaturePhrase>
            <p className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#B7901F] leading-tight tracking-tight mt-2">
              Vous êtes évalué sur ce que vous faites.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: MessageSquare, t: 'Échanges écrits' },
              { icon: Mic, t: 'Sessions vocales' },
              { icon: Target, t: 'Décisions en situation' },
            ].map((p) => (
              <motion.div
                key={p.t}
                {...fadeInUp}
                className="rounded-2xl bg-white border border-[#EDE7D7] p-6 hover:border-[#D4AF37] transition-all"
                style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
              >
                <p.icon className="w-5 h-5 text-[#B7901F] mb-3" />
                <p className="text-[#1C1147] font-semibold">{p.t}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center mt-8 text-[#7A6F58] italic">
            Toutes les données sont traçables · horodatées · exploitables.
          </p>
        </section>

        <SectionDivider />

        {/* ─── 8. TRANSFORMATION ─── */}
        <section className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-12">
            <Kicker>Transformation</Kicker>
            <SignaturePhrase>Ce n'est pas une amélioration.</SignaturePhrase>
            <p className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#B7901F] leading-tight tracking-tight mt-2">
              C'est un changement de posture.
            </p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-white border border-[#E5DFCE] p-8"
              style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#A89C7B] font-semibold mb-5">
                Avant CoachDIA
              </p>
              <ul className="space-y-3 text-[#7A6F58]">
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Manager submergé par l'opérationnel</li>
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Décisions lentes</li>
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Délégation approximative</li>
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Réunions peu efficaces</li>
              </ul>
            </motion.div>
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-gradient-to-br from-[#FFF8E0] to-white border-2 border-[#D4AF37] p-8"
              style={{ boxShadow: '0 8px 28px rgba(212,175,55,0.18)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-5">
                Après CoachDIA
              </p>
              <ul className="space-y-3 text-[#1C1147]">
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> <strong>Vision claire</strong></li>
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> <strong>Décisions rapides</strong></li>
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> <strong>Délégation structurée</strong></li>
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> <strong>Performance collective activée</strong></li>
              </ul>
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ─── 9. EXCLUSION ─── */}
        <section className="px-4 max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="max-w-3xl mb-10">
            <Kicker>Honnêteté radicale</Kicker>
            <SignaturePhrase>CoachDIA n'est pas pour tout le monde.</SignaturePhrase>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-6">
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-white border border-[#E5DFCE] p-8"
              style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.04)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#A89C7B] font-semibold mb-5">
                Ce n'est pas pour
              </p>
              <ul className="space-y-3 text-[#7A6F58]">
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Ceux qui cherchent du contenu passif</li>
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Ceux qui veulent « se sentir progresser »</li>
                <li className="flex gap-3"><XCircle className="w-4 h-4 text-[#A89C7B] mt-1 shrink-0" /> Ceux qui évitent la remise en question</li>
              </ul>
            </motion.div>
            <motion.div
              {...fadeInUp}
              className="rounded-3xl bg-gradient-to-br from-[#FFF8E0] to-white border-2 border-[#D4AF37] p-8"
              style={{ boxShadow: '0 8px 28px rgba(212,175,55,0.18)' }}
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#B7901F] font-semibold mb-5">
                C'est conçu pour
              </p>
              <ul className="space-y-3 text-[#1C1147]">
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Ceux qui veulent progresser concrètement</li>
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Ceux qui acceptent d'être challengés</li>
                <li className="flex gap-3"><CheckCircle className="w-4 h-4 text-[#B7901F] mt-1 shrink-0" /> Ceux qui veulent des résultats mesurables</li>
              </ul>
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ─── 10. TÉMOIGNAGES (dynamic, paginé, modéré) ─── */}
        <TestimonialsSection />

        <SectionDivider />

        {/* ─── 11. PRICING ─── */}
        <section
          id="plans"
          className="px-4 max-w-6xl mx-auto"
          data-testid="home-pricing-section"
        >
          <motion.div {...fadeInUp} className="text-center mb-12">
            <Kicker>Offres Parcours IA</Kicker>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#1C1147] leading-tight tracking-tight mb-3">
              Choisissez votre rythme
            </h2>
            <p className="text-[#5A5266]">Paiement sécurisé · Annulation à tout moment</p>
          </motion.div>
          <PricingGrid />
          <motion.div {...fadeInUp} className="text-center text-[#5A5266] space-y-1">
            <p className="text-sm">
              ✔ Coach IA vocal + écrit · ✔ Modules certifiants · ✔ Progression mesurable
            </p>
          </motion.div>
        </section>

        <SectionDivider />

        {/* ─── 12. FOOTER SIGNATURE ─── */}
        <section className="px-4 max-w-4xl mx-auto pb-20">
          <motion.div {...fadeInUp} className="text-center">
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3A3252] leading-tight tracking-tight">
              Former était insuffisant.
            </p>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#B7901F] leading-tight tracking-tight mt-2">
              Mesurer devient la norme.
            </p>
            <div
              className="mt-12 inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white border border-[#D4AF37]/40"
              style={{ boxShadow: '0 4px 18px rgba(28,17,71,0.06)' }}
            >
              <Headphones className="w-4 h-4 text-[#B7901F]" />
              <span className="font-serif text-base text-[#1C1147]">CoachDIA</span>
              <span className="text-[#5A5266]">·</span>
              <span className="text-sm text-[#5A5266] italic">Là où le leadership devient observable.</span>
            </div>
          </motion.div>
        </section>

        <Footer />
      </div>
      <FloatingButtons />
    </div>
  );
};

export default LandingPage;
